import { SetToothFullHeadMPRSliceEditorStateReq } from '@/shared/api/protocol-ts/api/core/svc_report_pb';
import { ToothFullHeadMPRSliceGeometry } from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';
import { describeToothBasisSlice } from '@/shared/graphics/medicalImageRender/MedicalImageRender';

import type { FullHeadMprOriginalSlice } from '@/features/MPR';

import type {
  FullHeadMprSliceGroupView,
  FullHeadMprSliceRecordView,
  FullHeadMprSliceTileView,
  ToothSliceGeometry,
  ToothSliceOptionsBySeries,
} from '../config';

import { makeFullHeadMprSliceGeometry } from './makeFullHeadMprSliceGeometry';
import { makeFullHeadMprSliceTarget } from './makeFullHeadMprSliceTarget';
import { makeToothBasisSeriesRequest } from './makeToothBasisSeriesRequest';
import { getFullHeadMprAnnotationFrame } from './transformFullHeadMprAnnotations';

export const loadFullHeadMprOriginalSlice = async ({
  group,
  input,
  record,
  signal,
  sliceOptions,
  tile,
  toothID,
}: {
  group: FullHeadMprSliceGroupView | null;
  input: ToothSliceGeometry | null;
  sliceOptions: ToothSliceOptionsBySeries;
  tile: FullHeadMprSliceTileView;
  toothID: string;
  record?: FullHeadMprSliceRecordView;
  signal?: AbortSignal;
}): Promise<FullHeadMprOriginalSlice> => {
  const frame = getFullHeadMprAnnotationFrame(record);
  const annotationOptions = record?.geometry
    ? {
        ...sliceOptions,
        [tile.seriesName]: {
          ...sliceOptions[tile.seriesName],
          thicknessMm: record.geometry.thicknessMm,
        },
      }
    : sliceOptions;
  const view =
    frame?.view ??
    (tile.kind === 'view'
      ? tile.view
      : input && group
        ? await describeToothBasisSlice(
            makeToothBasisSeriesRequest({
              input,
              sliceOptions: annotationOptions,
            }),
            {
              offsetMm: tile.offsetMm,
              seriesName: tile.seriesName,
              spacingOutMm:
                record?.geometry?.scaleMmPerPx ?? group.frame.spacingMm,
            },
            signal,
          )
        : null);
  signal?.throwIfAborted();
  if (!view) {
    throw new Error('The original MPR slice geometry is unavailable');
  }
  const geometry = record?.geometry
    ? new ToothFullHeadMPRSliceGeometry({
        GeometrySource: record.geometry.geometrySource,
        HeightPx: record.geometry.heightPx,
        WidthPx: record.geometry.widthPx,
        Scale: record.geometry.scaleMmPerPx,
        ThicknessMm: record.geometry.thicknessMm,
        VolumeFileID: record.geometry.volumeFileID,
      })
    : group && input
      ? makeFullHeadMprSliceGeometry({
          geometrySource: input.geometrySource,
          heightPx: group.frame.heightPx,
          scaleMmPerPx: group.frame.spacingMm,
          thicknessMm: group.slabThicknessMm,
          volumeFileID: input.volumeFileID,
          widthPx: group.frame.widthPx,
        })
      : null;
  if (!geometry) {
    throw new Error('The original MPR annotation raster is unavailable');
  }
  return {
    geometry,
    target: record
      ? { case: 'SliceID', value: record.recordID }
      : new SetToothFullHeadMPRSliceEditorStateReq(
          makeFullHeadMprSliceTarget({
            toothID,
            tile,
            sliceStepMm: group?.options.sliceStepMm ?? 0,
          }),
        ).Target,
    view,
  };
};
